/* eslint-disable react/jsx-props-no-spreading */
import "core-js/stable";
import "regenerator-runtime/runtime";
import "custom-event-polyfill";
import "url-search-params-polyfill";
import "intersection-observer";
import "firebase/messaging";
import "whatwg-fetch";
import "lazysizes";
import "lazysizes/plugins/attrchange/ls.attrchange";
import "@utils/languages";
import { withLDConsumer, asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import React, { Component } from "react";
import PropTypes from "prop-types";
import { render } from "react-dom";
import { Router, Route, IndexRoute, IndexRedirect, browserHistory, Redirect } from "react-router";
import { Provider } from "react-redux";
import { syncHistoryWithStore } from "react-router-redux";
import createStore from "@redux-core";

// SCSS and external styles
import "bootstrap/dist/css/bootstrap.min.css";
import "toastr/build/toastr.css";
import "animate.css";
import "../scss/app.scss";

import {
	ENV_OPTIONS,
	ADOPERATIONS,
	ROUTES,
	BUILD_ENV,
	APP_VERSION,
	ORIGIN_DOMAINS_REGEXP,
} from "@constants";
import { AppWrapper, AuthWrapper } from "@components/common";
import Reports from "@pages/reports/reports";

// msal.js
import { MsalProvider } from "@azure/msal-react";

// Services
import { MicrosoftAuthenticationService } from "@services";

// Datadog RUM
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

// Jobs
import { SplashScreen } from "../components/index";
import {
	JobsList,
	JobForm,
	JobDrafts,
	JobRequests,
	JobDetails,
	JobLocationDetails,
	JobEstimate,
	EstimatesList,
	Audit,
} from "../pages/jobs";

// Shifts
import ShiftList from "../pages/shifts/ShiftList";

// Notes
import NoteList from "../pages/notes/NoteList";

// Jurisdictions
import { JurisdictionList } from "../pages/jurisdictions";

// Error404
import Error404 from "../pages/error404/Error404";

// My Account
import { MyAccount } from "../pages/myAccount";

// Announcements
import { AnnouncementList, AnnouncementForm } from "../pages/announcements";

// Client Rates
import ClientRates from "../pages/clientRates/ClientRates";

// Clients
import { ClientList, ClientForm, ClientView } from "../pages/clients";

// LEA Location Approval
import { LocationList } from "../pages/locationApprovals";

// Location Details
import { LocationDetails } from "../pages/locationDetails";

// Super Admin Users
import Users from "../pages/users/UserList/UserList";

// Projects
import { ProjectList, ProjectDetails } from "../pages/projects";

// Staff
import { Staff } from "../pages/staff";

// Dashboaard
import { Dashboard } from "../pages/dashboard";

// Organizations
import { OrganizationsList, OrganizationDetails } from "../pages/organizations";

import { Groups } from "../pages/groups";

// LEA Job Approvals
import { JobApprovalList } from "../pages/jobApprovals";

// Admin Tax Exemption Approvals
import { TaxExemptionRequests } from "../pages/taxExemptionRequests";

// Agency Account
import { AgencyAccount } from "../pages/agencyAccount";

// Officers
import { OfficerList, OfficerDetails } from "../pages/officers";

// Login
import Login from "../pages/login/Login";

// Logout
import Logout from "../pages/logout/Logout";

// Sign Up
import { CoordinatorSignup, SignUpLea, SignUpAgency } from "../pages/signup";
import SignupRedirect from "../pages/signup/ADSignup/SignupRedirect";
import ADCallback from "../pages/signup/ADSignup/ADCallback";
import Confirmation from "../pages/confirmation/Confirmation";
import ADConfirmation from "../pages/signup/ADSignup/ADConfirmation";
import SignupLea from "../pages/signup/ADSignup/SignupLea";
import Activation from "../pages/activation/Activation";

// Payments
import {
	NonRKPayInvoiceList,
	NonRKPayInvoiceListV4,
	PaymentList,
	PaymentDetails,
	PaymentSummary,
	PaymentReceipt,
	BulkInvoicePreview,
	NonRKPayInvoiceDetail,
	PaymentSummaryV2,
	PaymentsRedirect,
} from "../pages/payments";

// Coordinator deposit history
import DepositHistory from "../pages/depositHistory/DepositHistory";
import FeatureFlagService from "./services/FeatureFlagService";

const store = createStore(window.history);
const history = syncHistoryWithStore(browserHistory, store);
const { msalInstance } = MicrosoftAuthenticationService;

browserHistory.listen(({ search }) => {
	// Only force a scroll to top on pagination changes
	if (search.indexOf("pageNumber") > 0) window.scrollTo(0, 0);
});

if (!localStorage.getItem("iu")) {
	datadogRum.init({
		service: ENV_OPTIONS.datadog.service,
		applicationId: ENV_OPTIONS.datadog.applicationId,
		clientToken: ENV_OPTIONS.datadog.clientToken,
		site: ENV_OPTIONS.datadog.site,
		allowedTracingUrls: ORIGIN_DOMAINS_REGEXP,
		env: BUILD_ENV,
		version: APP_VERSION,
		sessionSampleRate: ENV_OPTIONS.datadog.sampleRate,
		sessionReplaySampleRate: ENV_OPTIONS.datadog.premiumSampleRate,
		trackUserInteractions: ENV_OPTIONS.datadog.trackInteractions,
		defaultPrivacyLevel: ENV_OPTIONS.datadog.defaultPrivacyLevel,
		trackLongTasks: ENV_OPTIONS.datadog.trackLongTasks,
		trackResources: ENV_OPTIONS.datadog.trackResources,
	});

	datadogLogs.init({
		clientToken: ENV_OPTIONS.datadog.clientToken,
		site: ENV_OPTIONS.datadog.site,
		sessionSampleRate: ENV_OPTIONS.datadog.sampleRate,
		env: BUILD_ENV,
		version: APP_VERSION,
		service: ENV_OPTIONS.datadog.service,
	});

	datadogRum.startSessionReplayRecording();
}

// TODO Fix disabled eslint rules
// Not all of the rules require to be fixed, but some of them can have an alternative approach
// The rules involved are the following:
// react/no-array-index-key
// react/jsx-props-no-spreading
// jsx-a11y/mouse-events-have-key-events
// jsx-a11y/no-noninteractive-element-interactions
class App extends Component {
	constructor() {
		super();
		global.setLanguage("en");

		this.state = {
			initialized: false,
		};

		// set a token passed via 'auth' param, i.e. SSO redirect
		const { search, hash } = window.location;
		let authToken;
		if (search.indexOf("auth=token") > -1) {
			authToken = hash.substr(hash.indexOf("token=") + 6);
		}
		if (authToken) localStorage.setItem("t", authToken);
	}

	componentDidMount() {
		const {
			flags: { depositReport },
			ldClient,
		} = this.props;

		window.depositReportEnabledFlag = depositReport;

		if (ldClient) {
			FeatureFlagService.setClient(ldClient);
		}

		this.setState({ initialized: true });
	}

	shouldComponentUpdate = (nextProps, nextState) => {
		const { initialized } = this.state;

		return initialized !== nextState.initialized;
	};

	render() {
		const { initialized } = this.state;

		return (
			<Choose>
				<When condition={!initialized}>
					<SplashScreen />
				</When>
				<Otherwise>
					<Provider store={store}>
						<MsalProvider instance={msalInstance}>
							<AppWrapper>
								<Router history={history}>
									{/* Adding backward compatibility with the old signup urls */}
									<div>
										<Redirect
											from="/signup"
											to={ROUTES.signupSecurityAgencyAD}
										/>
										<Redirect
											from="/signup-lea"
											to={ROUTES.signupLea}
										/>
										<Redirect
											from="/signup-coordinator"
											to={ROUTES.signupCoordinatorAD}
										/>

										<Route
											path={ROUTES.ADCallback}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => <ADCallback {...props} />}
										/>
										<Route
											path={ROUTES.resetPassword}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => (
												<SignupRedirect
													{...props}
													redirectPolicy={
														ENV_OPTIONS.adb2c.policies.resetPassword
													}
													operation={ADOPERATIONS.resetPassword}
												/>
											)}
										/>
										<Route
											path={ROUTES.signupConfirmation}
											component={ADConfirmation}
										/>
										<Route
											path={ROUTES.signupCoordinatorAD}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => (
												<SignupRedirect
													{...props}
													redirectPolicy={
														ENV_OPTIONS.adb2c.policies.signupCoordinator
													}
													operation={ADOPERATIONS.signup}
												/>
											)}
										/>
										<Route
											path={ROUTES.signupSecurityAgencyAD}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => (
												<SignupRedirect
													{...props}
													redirectPolicy={
														ENV_OPTIONS.adb2c.policies
															.signupSecurityAgency
													}
													operation={ADOPERATIONS.signup}
												/>
											)}
										/>
										<Route
											path={ROUTES.signupLeaAD}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => (
												<SignupRedirect
													{...props}
													redirectPolicy={
														ENV_OPTIONS.adb2c.policies.signupLea
													}
													operation={ADOPERATIONS.signup}
													hiddenInputs={["jurisdictionId"]}
												/>
											)}
										/>
										<Route
											path={ROUTES.signupLea}
											// eslint-disable-next-line react/no-unstable-nested-components
											component={props => <SignupLea {...props} />}
										/>
									</div>

									<Route
										path="/"
										component={AuthWrapper}
									>
										<IndexRedirect to="jobs" />
										<Route
											path="staff"
											component={Staff}
										/>
										<Route
											path="reports"
											component={Reports}
										/>
										<Route path="officers">
											<IndexRoute component={OfficerList} />
											<Route
												path=":officerId"
												component={OfficerDetails}
											/>
										</Route>
										<Route
											path="users"
											component={Users}
										/>
										<Route
											path="my-account"
											component={MyAccount}
										/>
										<Route
											path="agency-account"
											component={AgencyAccount}
										/>
										<Route path="announcements">
											<IndexRoute component={AnnouncementList} />
											<Route
												path="add"
												component={AnnouncementForm}
											/>
											<Route path=":announcementId">
												<Route
													path="edit"
													component={AnnouncementForm}
												/>
											</Route>
										</Route>
										<Route path="payments">
											<IndexRoute component={PaymentsRedirect} />
											<Route
												path="manual"
												component={NonRKPayInvoiceList}
											/>
											<Route path="list">
												<IndexRedirect to="review" />
												<Route
													path=":viewType"
													component={PaymentList}
												/>
											</Route>
											<Route path="v4">
												<IndexRedirect to="estimates" />
												<Route
													path=":viewType"
													component={NonRKPayInvoiceListV4}
												/>
											</Route>
											<Route
												path="history"
												component={DepositHistory}
											/>
											<Route
												path="invoice"
												component={PaymentSummary}
											/>
											<Route
												path="invoices-summary"
												component={PaymentSummaryV2}
											/>
											<Route
												path="invoicepreview"
												component={BulkInvoicePreview}
											/>
											<Route
												path="manualinvoice"
												component={NonRKPayInvoiceDetail}
											/>
											<Route
												path=":invoiceId"
												component={PaymentDetails}
											/>
											<Route
												path="receipt/:id"
												component={PaymentReceipt}
											/>
										</Route>
										<Route path="clients">
											<IndexRoute component={ClientList} />

											<Route
												path="add"
												component={ClientForm}
											/>
											<Route
												path="rates"
												component={ClientRates}
											/>

											<Route
												path="form/:clientId"
												component={ClientForm}
											/>
											<Route
												path="view/:clientId"
												component={ClientView}
											/>
										</Route>
										<Route
											path="client-rates"
											component={ClientRates}
										/>
										<Route path="projects">
											<IndexRedirect to="list" />

											<Route
												path="list"
												component={ProjectList}
											/>
											<Route
												path="add"
												component={ProjectDetails}
											/>

											<Route path=":projectId">
												<IndexRoute component={ProjectDetails} />
											</Route>
										</Route>
										<Route path="admin">
											<IndexRedirect to="location" />

											<Route path="approvals">
												<IndexRedirect to="list" />

												<Route path="list">
													<IndexRedirect to="pending" />
													<Route
														path=":viewType"
														component={JobApprovalList}
													/>
												</Route>
											</Route>
											<Route path="taxExemptionRequests">
												<IndexRedirect to="list" />
												<Route
													path="list"
													component={TaxExemptionRequests}
												/>
											</Route>
											<Route path="location">
												<IndexRedirect to="list" />
												<Route
													path="list"
													component={LocationList}
												/>
											</Route>
											<Route path="shift">
												<IndexRedirect to="list" />
												<Route
													path="list"
													component={ShiftList}
												/>
											</Route>
										</Route>
										<Route path="jobs">
											<IndexRedirect to="list" />

											<Route path="list">
												<IndexRedirect to="active" />
												<Route
													path=":viewType"
													component={JobsList}
												/>
											</Route>

											<Route path="estimates">
												<IndexRedirect to="pending" />

												<Route path="detail">
													<Route
														path=":estimateId"
														component={JobEstimate}
													/>
												</Route>

												<Route
													path=":viewType"
													component={EstimatesList}
												/>
											</Route>

											<Route
												path="add"
												component={JobForm}
											/>
											<Route
												path="drafts"
												component={JobDrafts}
											/>
											<Route
												path="requests"
												component={JobRequests}
											/>
											<Route
												path="audit"
												component={Audit}
											/>
											<Route path=":jobId">
												<IndexRoute component={JobDetails} />
												<Route
													path="edit"
													component={JobForm}
												/>
												<Route
													path="locationDetails"
													component={JobLocationDetails}
												/>
											</Route>
										</Route>
										<Route path="locations">
											<Route
												path=":id"
												component={LocationDetails}
											/>
										</Route>
										<Route path="locationgroups">
											<Route
												path=":id"
												component={LocationDetails}
											/>
										</Route>
										<Route path="jurisdictions">
											<IndexRoute component={JurisdictionList} />
										</Route>
										<Route path="organizations">
											<IndexRoute component={OrganizationsList} />
											<Route
												path=":organizationId"
												component={OrganizationDetails}
											/>
										</Route>
										<Route path="groups">
											<Route
												path="list"
												component={Groups}
											>
												<IndexRedirect to="officers" />
												<Route
													path=":viewType"
													component={Groups}
												/>
											</Route>
										</Route>
										<Route
											path="notes"
											component={NoteList}
										/>
										<Route
											path="login"
											component={Login}
										/>
										<Route
											path="logout"
											component={Logout}
										/>
										<Route
											path="signup-lea"
											component={SignUpLea}
										/>
										<Route
											path="signup"
											component={SignUpAgency}
										/>
										<Route
											path="signup-coordinator"
											component={CoordinatorSignup}
										/>
										<Route
											path="confirmation"
											component={Confirmation}
										/>
										<Route
											path="activation"
											component={Activation}
										/>
										<Route
											path="dashboard"
											component={Dashboard}
										/>
										<Route
											path="404"
											component={Error404}
										/>
										<Route
											path="*"
											component={Error404}
										/>
									</Route>
								</Router>
							</AppWrapper>
						</MsalProvider>
					</Provider>
				</Otherwise>
			</Choose>
		);
	}
}

App.propTypes = {
	flags: PropTypes.object,
	ldClient: PropTypes.object,
};

App.defaultProps = {
	flags: {},
	ldClient: {},
};
const WrappedApp = withLDConsumer()(App);

(async () => {
	const LDProvider = await asyncWithLDProvider({
		clientSideID: ENV_OPTIONS.launchDarkly.clientSideID,
		options: {
			baseUrl: ENV_OPTIONS.launchDarkly.baseUrl,
			eventsUrl: ENV_OPTIONS.launchDarkly.eventsUrl,
			streamUrl: ENV_OPTIONS.launchDarkly.streamUrl,
			inspectors: [
				// This is used to track feature flag usage in Datadog
				//
				{
					type: "flag-used",
					name: "dd-inspector",
					method: (key, detail) => {
						datadogRum.addFeatureFlagEvaluation(key, detail.value);
					},
				},
			],
		},
	});

	render(
		<LDProvider>
			<WrappedApp />
		</LDProvider>,
		document.getElementById("app"),
	);
})();
